import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import {
  clearSession,
  getStoredToken,
  getStoredUser,
  storeSession,
  type AuthUser,
} from '@/auth/tokenStorage'
import { loginRequest, registerVendorRequest } from '@/api/authApi'

type AuthContextValue = {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  login: (login: string, password: string) => Promise<void>
  register: (payload: {
    username: string
    email: string
    password: string
    full_name: string
    whatsapp_number: string
    business_name?: string
  }) => Promise<void>
  updateStoredUser: (user: AuthUser) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Provides session state backed by localStorage + JWT from the API.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken())
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser())

  const login = useCallback(async (loginValue: string, password: string) => {
    const result = await loginRequest({ login: loginValue, password })
    storeSession(result.token, result.user)
    setToken(result.token)
    setUser(result.user)
  }, [])

  const register = useCallback(
    async (payload: {
      username: string
      email: string
      password: string
      full_name: string
      whatsapp_number: string
      business_name?: string
    }) => {
      const result = await registerVendorRequest(payload)
      storeSession(result.token, result.user)
      setToken(result.token)
      setUser(result.user)
    },
    [],
  )

  const updateStoredUser = useCallback((next: AuthUser) => {
    const currentToken = getStoredToken()
    if (currentToken) {
      storeSession(currentToken, next)
    }
    setUser(next)
  }, [])

  const logout = useCallback(() => {
    clearSession()
    setToken(null)
    setUser(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isAdmin: user?.role === 'admin',
      login,
      register,
      updateStoredUser,
      logout,
    }),
    [user, token, login, register, updateStoredUser, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
